/**
 * Phase 1-D — Agency Portal identity API client.
 *
 * Thin fetch wrapper matching the existing codebase convention (see
 * `src/lib/api/app-init.ts`). RTK Query is intentionally NOT used here
 * because no other slice in this repo uses it yet; introducing it for
 * a single scaffold would create an inconsistent pattern. Phase 6 may
 * migrate everything to RTK Query in one pass.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * Per CLAUDE.md, this token lives in Redux ONLY and is never persisted.
 *
 * Base URL: `CENTRAL_API_BASE_URL` from `src/lib/constants/central-api.ts`,
 * which already resolves to `${env-base}/api/v1/` across DEV/STAGING/LIVE.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';
import type { McpAccess, PlatformDef } from '../roles/roles-api';
import type {
  AgencyAccessibleBrand,
  AgencyAccessibleShop,
  AgencyEmployeeIdentity,
  NeedsLegacyReconnectEntry,
} from '../../../store/reducers/authSlice';

export type { McpAccess, PlatformDef } from '../roles/roles-api';

// ---------- shared types ----------

export interface IdentityMeResponse extends AgencyEmployeeIdentity {
  accessible_shops: AgencyAccessibleShop[];
  // Phase 8-H — backend now returns the account type (`agency` | `enterprise`)
  // and an `accessible_brands` alias collection. Both are optional on the
  // wire for backwards compatibility; UI relabel logic defaults to `agency`.
  account_type?: 'agency' | 'enterprise' | string;
  accessible_brands?: AgencyAccessibleBrand[];
  // Phase 9-D — Slack reconnect surface. Each entry is a (shop, platform)
  // pair that still has an active legacy Phase 8-B row but no replacement
  // gateway-OAuth row. Drives the dashboard banner.
  needs_legacy_reconnect?: NeedsLegacyReconnectEntry[];
  // MCP-client OAuth (TARA_PLUGIN_OAUTH2_PLAN.md D13) — how the request
  // authenticated (`api_key` = emp_ key, `oauth` = mcpat_ access token) and,
  // for OAuth, the session (token family) it belongs to. Absent on older
  // backends.
  auth_via?: 'api_key' | 'oauth' | string;
  session_id?: string | null;
}

export interface AgencyEmployee {
  id: string;
  email: string;
  name: string;
  role: string;
  is_team_lead?: boolean;
  active?: boolean;
  assigned_shops?: AgencyAccessibleShop[];
  group_id?: string | null;
  // Phase 8-J — null until the invitee accepts via the magic-link flow.
  invitation_accepted_at?: string | null;
  // Set together with the invitation token on invite and resend; null on a
  // row that was never invited (the account holder's own).
  invitation_token_expires_at?: string | null;
  /**
   * Per-member MCP platform allowlist ("Jerrick → Google Ads only, all brands").
   * Backend always returns the resolved shape ({ mode: 'all' } or
   * { mode: 'allowlist', platforms: [...] }); a missing value is treated as
   * `{ mode: 'all' }` (inherit the role). Governs which platforms this member's
   * AI tools may reach — NOT what they can see/connect in the portal.
   */
  mcp_access?: McpAccess;
}

// Request body for POST /agency/employees — "add employee & generate key now".
// Mirrors the server's StoreEmployeeRequest field-for-field.
export interface CreateEmployeeRequest {
  email: string;
  name: string;
  // Optional — the server defaults to 'analyst' when omitted.
  role?: string;
  team_lead_id?: string;
  is_active?: boolean;
  // Brands granted at creation. A brand in `shop_slugs` gets the role's
  // default level; `shop_permissions` carries an explicit level and WINS for a
  // slug that appears in both. Slugs the acting org doesn't own are silently
  // ignored server-side.
  shop_slugs?: string[];
  shop_permissions?: {
    slug: string;
    permission_level: 'read' | 'propose' | 'approve';
  }[];
  // Optional per-member platform allowlist. Omit (or send { mode: 'all' }) to
  // have the member inherit the role.
  mcp_access?: McpAccess;
}

// Response 201 from POST /agency/employees. This endpoint hands the plaintext
// `emp_` key over immediately (no magic-link to redeem), so the key is shown
// ONCE — it is never stored and never re-fetchable.
export interface CreateEmployeeResponse {
  // The created member, with resolved mcp_access.
  data: AgencyEmployee;
  // Plaintext `emp_` key — shown ONCE.
  api_key: string;
  api_key_hint: string;
  // Canonical org plan-gate flag (same source as `/me`'s `has_active_plan`).
  // When false the member's AI tools stay empty until the org has an active
  // plan — the key still authenticates, it just has no tools to reach yet.
  has_active_plan: boolean;
  warning: string;
}

// Phase 8-J — magic-link invite request body. Superset of CreateEmployeeRequest;
// `team_lead_id` and `shop_slugs` are optional.
export interface InviteEmployeeRequest {
  email: string;
  name: string;
  role: string;
  // A brand named here without a level gets the role's default level.
  shop_slugs?: string[];
  // Brands with an explicit level; wins over `shop_slugs` for the same slug.
  shop_permissions?: {
    slug: string;
    permission_level: 'read' | 'propose' | 'approve';
  }[];
  team_lead_id?: string;
  // Optional per-member platform allowlist set when adding the member. Omit (or
  // send { mode: 'all' }) to have them inherit the role.
  mcp_access?: McpAccess;
  // An address that already has a pending invitation is refused with 409
  // `invitation_pending` (the error carries `reason` and the pending
  // `employee`). Send true to replace that invitation: the old link stops
  // working. Only ever set after the user chose to replace.
  replace_pending?: boolean;
}

// Phase 8-J — response from POST /agency/employees/invite. The plaintext API
// key is intentionally NOT here — it's only ever returned by the accept
// endpoint when the invitee redeems the link.
export interface EmployeeInvitation {
  employee: AgencyEmployee;
  invite_url: string;
  expires_at: string;
  email_sent: boolean;
}

// Phase 8-J — response from POST /agency/employees/accept. This is the ONLY
// endpoint that returns `api_key` in plaintext, and only once per invite token.
export interface EmployeeAcceptResponse {
  api_key: string;
  api_key_hint: string;
  employee: AgencyEmployee;
  warning: string;
}

export interface UpdateEmployeeRequest {
  name?: string;
  role?: string;
  is_team_lead?: boolean;
  active?: boolean;
  // Per-member platform allowlist. Send { mode: 'all' } (or null) to clear a
  // restriction; { mode: 'allowlist', platforms: [...] } to scope the member.
  mcp_access?: McpAccess | null;
}

export interface AgencyGroup {
  id: string;
  name: string;
  // Phase 13 — role the group's members inherit (null/absent = none).
  role?: string | null;
  team_lead_id?: string | null;
  // The lead as the list returns it. The answer to a create carries only
  // `team_lead_id`.
  team_lead?: { id: string; name?: string | null; email?: string | null } | null;
  // Not sent by the API; kept for data that carries the name directly.
  team_lead_name?: string | null;
  member_count?: number;
  members?: AgencyEmployee[];
}

export interface CreateGroupRequest {
  name: string;
  team_lead_id?: string;
  // Phase 13 — optional role assigned to the group on creation.
  role?: string | null;
}

// ---------- internal helpers ----------

function getAuthHeader(): Record<string, string> {
  const token = store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function tokenOverrideHeader(token: string): Record<string, string> {
  // Used only by validateApiKey BEFORE the token is written to Redux.
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[identity-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

/** The `errors` object of a refusal as field -> sentences, or undefined. */
export function fieldErrorsFrom(
  errors: unknown
): Record<string, string[]> | undefined {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return undefined;
  }
  const out: Record<string, string[]> = {};
  Object.entries(errors as Record<string, unknown>).forEach(([field, v]) => {
    const list = (Array.isArray(v) ? v : [v]).filter(
      (x): x is string => typeof x === 'string' && x.trim() !== ''
    );
    if (list.length) out[field] = list;
  });
  return Object.keys(out).length ? out : undefined;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  authOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...(authOverride
      ? tokenOverrideHeader(authOverride)
      : getAuthHeader()),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
    // Phase B — send the httpOnly agency-session cookie on every identity/agency
    // call. This is what lets `/me` authenticate on a cold reload (when the
    // in-memory Bearer token is gone) and keeps every subsequent agency request
    // authenticated off the cookie.
    credentials: 'include',
  });

  if (!response.ok) {
    let detail = '';
    let code = '';
    let reason = '';
    let employee: unknown;
    let fieldErrors: Record<string, string[]> | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        reason?: string;
        employee?: unknown;
        errors?: unknown;
      }>(response);
      detail = body?.message || body?.error || '';
      if (typeof body?.error === 'string') code = body.error;
      if (typeof body?.reason === 'string') reason = body.reason;
      employee = body?.employee;
      fieldErrors = fieldErrorsFrom(body?.errors);
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & {
      status?: number;
      code?: string;
      reason?: string;
      employee?: unknown;
      fieldErrors?: Record<string, string[]>;
    };
    err.status = response.status;
    // 409 invitation_pending names the member whose invitation is pending.
    if (employee && typeof employee === 'object') err.employee = employee;
    // A refusal can carry a machine code (`error`) and a sentence for the user
    // (`reason`), e.g. 403 insufficient_rank / 422 last_owner on team requests.
    if (code) err.code = code;
    if (reason) err.reason = reason;
    // A 422 can name the refused field (`errors: { email: ['...'] }`).
    if (fieldErrors) err.fieldErrors = fieldErrors;
    throw err;
  }

  return parseJson<T>(response);
}

// ---------- identity endpoints ----------

/** GET /api/v1/me — validate the supplied token and return the identity card. */
export async function fetchIdentity(
  tokenOverride?: string
): Promise<IdentityMeResponse> {
  return request<IdentityMeResponse>('me', { method: 'GET' }, tokenOverride);
}

export interface RegenerateApiKeyResponse {
  api_key: string;
  api_key_hint: string;
  warning?: string;
}

/**
 * POST /agency/employees/me/regenerate-key — the ONLY thing that changes an
 * employee's `emp_` key.
 *
 * Signing in deliberately does NOT rotate (central-api
 * `SellerEmployeeBridgeService`): the key is also pasted into the Claude
 * plugin, MCP connectors and scheduled automations, and rotating it on every
 * login is what produced the "the API disconnects every day" reports. So this
 * call is destructive by design — the previous key stops working the moment it
 * returns, and every holder of it must be updated.
 *
 * The response also re-issues the `tara_session` cookie, so the caller's own
 * portal session survives; the new plaintext must be pushed into Redux by the
 * caller (it is never persisted to web storage).
 */
export async function regenerateOwnApiKey(): Promise<RegenerateApiKeyResponse> {
  return request<RegenerateApiKeyResponse>(
    'agency/employees/me/regenerate-key',
    { method: 'POST' }
  );
}

// ---------- MCP-client OAuth (TARA_PLUGIN_OAUTH2_PLAN.md WS3) ----------
//
// The gateway (gateway.adup.io) is the OAuth issuer the Claude plugin / MCP
// connectors talk to. It 302s the browser to the portal's `/oauth/authorize`
// consent page, which calls these endpoints with the employee's normal portal
// auth. central-api mints the single-use code and, later, the tokens; the
// portal never sees a token. "Connected devices" lists the employee's own
// OAuth sessions (one per authorization) and can sign them out.

export interface McpOAuthClientInfo {
  client_id: string;
  client_name: string;
  scope: string;
}

export interface McpOAuthAuthorizeParams {
  client_id: string;
  redirect_uri: string;
  state?: string;
  code_challenge: string;
  code_challenge_method?: string;
  resource?: string;
  scope?: string;
  decision?: 'approve' | 'deny';
}

export interface McpOAuthSession {
  family_id: string;
  client_name: string | null;
  created_at: string | null;
  last_used_at: string | null;
  access_expires_at: string | null;
  refresh_expires_at: string | null;
  is_current: boolean;
  ip_hint: string | null;
  user_agent_hint: string | null;
}

/** GET /oauth/client?client_id= — the name the consent page shows. 404 = unknown client. */
export async function fetchMcpOAuthClient(
  clientId: string
): Promise<McpOAuthClientInfo> {
  return request<McpOAuthClientInfo>(
    `oauth/client?client_id=${encodeURIComponent(clientId)}`,
    { method: 'GET' }
  );
}

/**
 * POST /oauth/authorize — Approve (default) or Deny the sign-in request.
 * Returns the full `redirect_to` URL; the caller navigates the browser there
 * (`window.location.assign`). Malformed requests reject with an RFC 6749
 * error — never a redirect.
 */
export async function authorizeMcpClient(
  params: McpOAuthAuthorizeParams
): Promise<{ redirect_to: string }> {
  return request<{ redirect_to: string }>('oauth/authorize', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/** GET /me/oauth-sessions — the caller's live OAuth sessions (devices). */
export async function listMcpOAuthSessions(): Promise<McpOAuthSession[]> {
  const res = await request<{ data?: McpOAuthSession[] }>('me/oauth-sessions', {
    method: 'GET',
  });
  return Array.isArray(res?.data) ? res.data : [];
}

/** DELETE /me/oauth-sessions/{family} — sign one of the caller's own devices out. */
export async function revokeMcpOAuthSession(familyId: string): Promise<void> {
  await request<unknown>(`me/oauth-sessions/${encodeURIComponent(familyId)}`, {
    method: 'DELETE',
  });
}

// ---------- brands ----------

export interface EmployeeCreateShopResponse {
  success: boolean;
  data?: { id: number; slug: string; name: string };
  message?: string;
  // True when the organisation already had a brand with this name and the
  // server returned it instead of creating one.
  already_exists?: boolean;
}

/**
 * POST /agency/shops — employee-authenticated brand creation (hotfix
 * 2026-08-06). The seller-side POST /sellers/{id}/shops needs a seller JWT,
 * which invite-only owner/manager sessions never hold; this is their
 * counterpart. Optionally persists the webshop URL in the same call because
 * the follow-up shop-general settings PUT is seller-JWT-gated too.
 */
export async function createShopAsEmployee(
  name: string,
  webshopUrl?: string
): Promise<EmployeeCreateShopResponse> {
  return request<EmployeeCreateShopResponse>('agency/shops', {
    method: 'POST',
    body: JSON.stringify({
      name,
      ...(webshopUrl ? { webshop_url: webshopUrl } : {}),
    }),
  });
}

// ---------- employees ----------

export async function listEmployees(): Promise<AgencyEmployee[]> {
  const res = await request<
    | { data?: AgencyEmployee[]; employees?: AgencyEmployee[] }
    | AgencyEmployee[]
  >('agency/employees', { method: 'GET' });
  // Backend EmployeeController::index returns `{ data: [...] }`. Older shapes
  // (`{ employees }` or a bare array) are still accepted so this is robust to
  // either envelope — otherwise the Team page renders empty on a 200.
  return Array.isArray(res) ? res : res.data ?? res.employees ?? [];
}

/**
 * List employees together with the MCP-connector platform catalog the Team
 * drawer's "Platform access" editor needs (FE keys + labels, incl. `custom`).
 * `all_platforms` is served alongside `data` by EmployeeController::index; a
 * pre-feature backend (or a bare-array envelope) yields an empty catalog, which
 * the drawer renders as "no platforms to choose" rather than crashing.
 */
export async function listEmployeesWithPlatforms(): Promise<{
  employees: AgencyEmployee[];
  all_platforms: PlatformDef[];
}> {
  const res = await request<
    | { data?: AgencyEmployee[]; employees?: AgencyEmployee[]; all_platforms?: PlatformDef[] }
    | AgencyEmployee[]
  >('agency/employees', { method: 'GET' });

  if (Array.isArray(res)) {
    return { employees: res, all_platforms: [] };
  }
  return {
    employees: res.data ?? res.employees ?? [],
    all_platforms: res.all_platforms ?? [],
  };
}

/**
 * POST /agency/employees — create a member AND generate their `emp_` key in one
 * step ("add employee & generate key now"), for owners/managers who want to
 * hand a teammate a working key immediately rather than send a magic-link
 * invite. The response carries the plaintext key exactly ONCE.
 *
 * This is the counterpart to `inviteEmployee()`, not a replacement — the invite
 * flow keeps key generation off the inviter's machine and is still the default.
 * Use this only when the inviter will hand the key over directly. An already
 * active, accepted member for this email is refused ({ error:
 * 'employee_already_exists' }, 422).
 */
export async function createEmployee(
  body: CreateEmployeeRequest,
  token?: string
): Promise<CreateEmployeeResponse> {
  return request<CreateEmployeeResponse>(
    'agency/employees',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
}

/**
 * Phase 8-J — invite an employee via the magic-link flow.
 *
 * Backend creates the employee in a pending state and emits an invitation
 * token. The response contains the `invite_url` to forward to the invitee
 * (also auto-emailed when `email_sent === true`) and the link's `expires_at`.
 * The plaintext API key is NOT in this response — it is only generated when
 * the invitee redeems the link via `acceptEmployeeInvitation()`.
 */
export async function inviteEmployee(
  body: InviteEmployeeRequest,
  token?: string
): Promise<EmployeeInvitation> {
  return request<EmployeeInvitation>(
    'agency/employees/invite',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
}

/**
 * Phase 8-J — redeem an invitation token.
 *
 * PUBLIC endpoint — the `invitation_token` IS the auth, so no Bearer header
 * is sent by default. Calling this endpoint mints the plaintext API key,
 * which is the ONLY time the key is ever revealed. The token is single-use
 * and is burned on success; a second call with the same token returns 4xx.
 */
export async function acceptEmployeeInvitation(
  invitationToken: string,
  password?: string,
  token?: string
): Promise<EmployeeAcceptResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${CENTRAL_API_BASE_URL}agency/employees/accept`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ token: invitationToken, password }),
      // Store the httpOnly `tara_session` cookie the API sets on accept.
      // Without it the freshly accepted invitee holds their key in memory only,
      // so the first reload logs them straight back out.
      credentials: 'include',
    }
  );

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<EmployeeAcceptResponse>(response);
}

/**
 * Phase 8-J — re-send an existing invitation (rotates the token and bumps
 * the expiry). Same auth requirements as `inviteEmployee()`.
 */
export async function resendEmployeeInvitation(
  employeeId: string,
  token?: string
): Promise<EmployeeInvitation> {
  return request<EmployeeInvitation>(
    `agency/employees/${employeeId}/resend-invitation`,
    { method: 'POST' },
    token
  );
}

export async function updateEmployee(
  employeeId: string,
  body: UpdateEmployeeRequest
): Promise<AgencyEmployee> {
  return request<AgencyEmployee>(`agency/employees/${employeeId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteEmployee(employeeId: string): Promise<void> {
  await request<unknown>(`agency/employees/${employeeId}`, {
    method: 'DELETE',
  });
}

export async function assignShopToEmployee(
  employeeId: string,
  shopSlug: string,
  // Canonical backend vocabulary: read | propose | approve. The old
  // 'manager'/'analyst'/'read_only' labels fail backend validation.
  permissionLevel?: 'read' | 'propose' | 'approve'
): Promise<void> {
  await request<unknown>(`agency/employees/${employeeId}/shops`, {
    method: 'POST',
    body: JSON.stringify({
      shop_slug: shopSlug,
      permission_level: permissionLevel ?? 'approve',
    }),
  });
}

export async function revokeShopFromEmployee(
  employeeId: string,
  shopSlug: string
): Promise<void> {
  await request<unknown>(`agency/employees/${employeeId}/shops/${shopSlug}`, {
    method: 'DELETE',
  });
}

// ---------- groups ----------

export async function listGroups(): Promise<AgencyGroup[]> {
  const res = await request<
    | { data?: AgencyGroup[]; groups?: AgencyGroup[] }
    | AgencyGroup[]
  >('agency/groups', { method: 'GET' });
  // Backend GroupController::index returns `{ data: [...] }`. Accept the older
  // `{ groups }`/bare-array shapes too so the Team page never renders empty on
  // a successful response.
  return Array.isArray(res) ? res : res.data ?? res.groups ?? [];
}

export async function createGroup(
  body: CreateGroupRequest
): Promise<AgencyGroup> {
  return request<AgencyGroup>('agency/groups', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Phase 13 — update a group's name / team lead / inherited role.
export async function updateGroup(
  groupId: string,
  body: { name?: string; team_lead_id?: string; role?: string | null }
): Promise<AgencyGroup> {
  return request<AgencyGroup>(`agency/groups/${groupId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Delete a team. Its members stay in the organisation and only lose the team.
 * Owner and manager only; a 403 `insufficient_rank` carries the `reason`.
 */
export async function deleteGroup(groupId: string): Promise<void> {
  await request<unknown>(`agency/groups/${groupId}`, { method: 'DELETE' });
}

export async function addGroupMember(
  groupId: string,
  employeeId: string
): Promise<void> {
  await request<unknown>(`agency/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({ employee_id: employeeId }),
  });
}

export async function removeGroupMember(
  groupId: string,
  employeeId: string
): Promise<void> {
  await request<unknown>(
    `agency/groups/${groupId}/members/${employeeId}`,
    { method: 'DELETE' }
  );
}

// ---------- employee email + password sign-in ----------

/**
 * Response shape of the three employee auth endpoints.
 *
 * `access_token` is always null: an invited employee has no legacy seller
 * account, so there is no seller JWT to mint. The portal runs on
 * `agency_api_key` (employee bearer) alone for these users.
 */
export interface EmployeeLoginResponse {
  access_token: null;
  user: null;
  agency_api_key: string;
  api_key_hint?: string;
  employee?: {
    id: string;
    email: string;
    name: string;
    role: string;
    organisation_id: string;
  };
}

/**
 * POST agency/employees/{login,forgot-password,reset-password}.
 *
 * PUBLIC endpoints — no Bearer header. `credentials: 'include'` so the httpOnly
 * `tara_session` cookie the API sets on success is stored, which is what keeps
 * the session alive across reloads.
 */
async function publicEmployeeAuthRequest<T>(
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  const json = await parseJson<{
    success?: boolean;
    message?: string;
    error?: string;
    data?: T;
  }>(response);

  if (!response.ok) {
    const err = new Error(
      json?.message || json?.error || `Request failed (${response.status})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return json?.data as T;
}

/**
 * Sign in as an agency EMPLOYEE with the email + password they chose when they
 * accepted their invite.
 *
 * The invite-only counterpart to the seller login. `POST /api/v1/login`
 * authenticates the legacy `users` table, which an invited employee has no row
 * in — so before this endpoint existed their credentials worked nowhere.
 */
export async function employeeLogin(
  email: string,
  password: string
): Promise<EmployeeLoginResponse> {
  return publicEmployeeAuthRequest<EmployeeLoginResponse>(
    'agency/employees/login',
    { email, password }
  );
}

/**
 * Request a password-reset link for an employee.
 *
 * Always resolves — the API answers 200 whether or not the address matches, so
 * it cannot be used to probe which accounts exist. Owners are deliberately not
 * covered: they sign in through the seller login and already have a working
 * seller-side reset.
 */
export async function employeeForgotPassword(email: string): Promise<void> {
  await publicEmployeeAuthRequest<unknown>(
    'agency/employees/forgot-password',
    { email }
  );
}

/**
 * Redeem an employee password-reset token and sign straight in.
 *
 * The token is one-shot; the response carries a freshly minted `emp_` key.
 */
export async function employeeResetPassword(
  token: string,
  password: string
): Promise<EmployeeLoginResponse> {
  return publicEmployeeAuthRequest<EmployeeLoginResponse>(
    'agency/employees/reset-password',
    { token, password }
  );
}
